import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { ApiError } from '@/shared/api';
import { useAuthActions } from '../model/use-auth-actions';

const schema = z
  .object({
    displayName: z.string().max(100).optional(),
    email: z.string().min(1, 'auth.emailRequired').email('auth.emailInvalid'),
    password: z
      .string()
      .min(8, 'auth.passwordMin')
      .regex(/[a-z]/, 'auth.passwordLower')
      .regex(/[A-Z]/, 'auth.passwordUpper')
      .regex(/\d/, 'auth.passwordDigit'),
    confirmPassword: z.string(),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'auth.passwordsDiffer',
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof schema>;

export function RegisterForm() {
  const { t } = useTranslation();
  const { register: registerUser } = useAuthActions();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { displayName: '', email: '', password: '', confirmPassword: '' },
  });

  const onSubmit = async (values: FormValues) => {
    setFormError(null);

    try {
      await registerUser({
        email: values.email,
        password: values.password,
        displayName: values.displayName || undefined,
      });
      navigate('/', { replace: true });
    } catch (error) {
      setFormError(
        error instanceof ApiError && error.errors.length > 0
          ? error.errors.join(' ')
          : t('auth.registerFailed'),
      );
    }
  };

  return (
    <Stack component="form" spacing={2.5} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Typography variant="h5">{t('auth.createAccount')}</Typography>

      {formError && <Alert severity="error">{formError}</Alert>}

      <TextField
        label={t('auth.displayName')}
        error={Boolean(errors.displayName)}
        helperText={errors.displayName?.message ? t(errors.displayName.message) : undefined}
        {...register('displayName')}
      />

      <TextField
        label={t('auth.email')}
        type="email"
        autoComplete="email"
        error={Boolean(errors.email)}
        helperText={errors.email?.message ? t(errors.email.message) : undefined}
        {...register('email')}
      />

      <TextField
        label={t('auth.password')}
        type="password"
        autoComplete="new-password"
        error={Boolean(errors.password)}
        helperText={errors.password?.message ? t(errors.password.message) : undefined}
        {...register('password')}
      />

      <TextField
        label={t('auth.confirmPassword')}
        type="password"
        autoComplete="new-password"
        error={Boolean(errors.confirmPassword)}
        helperText={errors.confirmPassword?.message ? t(errors.confirmPassword.message) : undefined}
        {...register('confirmPassword')}
      />

      <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
        {t(isSubmitting ? 'auth.creating' : 'auth.createButton')}
      </Button>

      <Typography variant="body2" color="text.secondary">
        {t('auth.alreadyHaveAccount')}{' '}
        <Link component={RouterLink} to="/login">
          {t('auth.signIn')}
        </Link>
      </Typography>
    </Stack>
  );
}
